import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { map, of, take, timeout } from 'rxjs';

import { LanguageService } from '@mobi/rwc-utils-ng-jslib';

import { NavigationService } from '../navigation.service';

import { LocationService } from './location.service';

const OLD_DOMAIN_PREFIX = 'mcs-schadenmeldung-b2c.app.';
const DEFAULT_LANGUAGE: Language = 'de';
const LANGUAGE_TIMEOUT_MS = 5000;

type Language = 'de' | 'fr' | 'it';

const DOMAIN_MAP: Record<Language, string> = {
    de: 'schadenmeldung.mobiliar',
    fr: 'declaration-de-sinistre.mobiliere',
    it: 'notifica-di-sinistro.mobiliare',
};

export function resolveNewDomain(host: string, language: string): string | undefined {
    if (!host.startsWith(OLD_DOMAIN_PREFIX)) {
        return undefined;
    }
    const isPreprod = host.includes('-preprod');
    const lang = (['de', 'fr', 'it'].includes(language) ? language : 'de') as Language;
    const baseDomain = DOMAIN_MAP[lang];
    return isPreprod ? `${baseDomain}-preprod.ch` : `${baseDomain}.ch`;
}

export const domainRedirectGuard: CanActivateFn = (route, state) => {
    const locationService = inject(LocationService);
    const languageService = inject(LanguageService);
    const navigationService = inject(NavigationService);

    const host = locationService.host;

    if (!host.startsWith(OLD_DOMAIN_PREFIX)) {
        return of(true);
    }

    // Query param language takes precedence over the language service value,
    // because the URL language reflects what the user explicitly requested.
    const queryParamLanguage = route.queryParams?.language;

    return languageService.language$.pipe(
        take(1),
        timeout({ first: LANGUAGE_TIMEOUT_MS, with: () => of(DEFAULT_LANGUAGE) }),
        map(language => {
            const effectiveLanguage = queryParamLanguage || language;
            const newDomain = resolveNewDomain(host, effectiveLanguage);
            if (newDomain) {
                navigationService.locationHref(`https://${newDomain}${state.url}`);
                return false;
            }
            return true;
        }),
    );
};
